import {
  Component,
  createSignal,
  createMemo,
  For,
  Show,
  onMount,
  onCleanup,
} from "solid-js";
import { Button, Spinner } from "../ui";
import { CsvDropzone, CsvMappingForm, GithubIssuePicker } from "../import";
import { buildCsvMapping, type PartialCsvMapping } from "../../utils/csv";
import { api } from "../../api/client";
import { surfaceQuotaExhausted } from "../../utils/aiError";
import {
  triggerAnalysis,
  getAnalysis,
  type RepoDebtFlag,
} from "../../api/repoAnalysis";
import {
  fetchGithubIssues,
  importGithubIssues,
  importCsv,
  type GitHubIssue,
  type GitHubIssueImportItem,
} from "../../api/workItems";
import type {
  WorkItemType,
  Priority,
  CreateWorkItemRequest,
} from "../../api/types";

export interface OnboardingAnalyzeStepProps {
  projectId: string;
  repoId?: string;
  repoOwner?: string;
  repoName?: string;
  onComplete: () => void;
  onSkip: () => void;
}

type StepState =
  | "detecting"
  | "analyzing"
  | "results"
  | "no-repo"
  | "csv-upload"
  | "csv-mapping"
  | "importing"
  | "done";

const SEVERITY_TO_PRIORITY: Record<string, Priority> = {
  critical: "high",
  high: "medium",
  medium: "low",
  low: "none",
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: "bg-signal-red-tint text-signal-red border-signal-red/25",
  high: "bg-signal-amber-tint text-signal-amber border-signal-amber/25",
  medium: "bg-signal-amber-tint text-ink-2 border-signal-amber/25",
  low: "bg-sunken text-ink-2 border-line",
};

export const OnboardingAnalyzeStep: Component<OnboardingAnalyzeStepProps> = (
  props,
) => {
  const [stepState, setStepState] = createSignal<StepState>("detecting");
  const [error, setError] = createSignal<string | null>(null);

  const [progressPct, setProgressPct] = createSignal(0);
  const [progressIndeterminate, setProgressIndeterminate] = createSignal(false);
  let progressTimer: ReturnType<typeof setInterval> | null = null;

  const PROGRESS_STAGES = [
    { at: 0, label: "Fetching repository metadata..." },
    { at: 15, label: "Scanning file tree..." },
    { at: 30, label: "Reading key files..." },
    { at: 50, label: "Analyzing architecture..." },
    { at: 70, label: "Detecting technical debt..." },
    { at: 85, label: "Inferring business context..." },
    { at: 95, label: "Finishing up..." },
  ];

  const progressLabel = createMemo(() => {
    const pct = progressPct();
    let label = PROGRESS_STAGES[0].label;
    for (const stage of PROGRESS_STAGES) {
      if (pct >= stage.at) label = stage.label;
    }
    return label;
  });

  const startProgressTimer = () => {
    setProgressPct(0);
    setProgressIndeterminate(false);
    const startTime = Date.now();
    const duration = 60_000; // 60 seconds

    progressTimer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      if (elapsed >= duration) {
        setProgressIndeterminate(true);
        if (progressTimer) {
          clearInterval(progressTimer);
          progressTimer = null;
        }
      } else {
        // Ease-out curve so it slows as it approaches 100%
        const linear = elapsed / duration;
        const eased = 1 - Math.pow(1 - linear, 2);
        setProgressPct(Math.round(eased * 100));
      }
    }, 250);
  };

  const stopProgressTimer = () => {
    if (progressTimer) {
      clearInterval(progressTimer);
      progressTimer = null;
    }
  };

  const [analysisSummary, setAnalysisSummary] = createSignal<string | null>(
    null,
  );
  const [debtFlags, setDebtFlags] = createSignal<RepoDebtFlag[]>([]);
  const [selectedFlags, setSelectedFlags] = createSignal<Set<string>>(
    new Set(),
  );

  const [issues, setIssues] = createSignal<GitHubIssue[]>([]);
  const [selectedIssues, setSelectedIssues] = createSignal<Set<number>>(
    new Set(),
  );

  // Work item types (for creating debt flag items)
  const [itemTypes, setItemTypes] = createSignal<WorkItemType[]>([]);

  const [importedCount, setImportedCount] = createSignal(0);
  const [importErrors, setImportErrors] = createSignal<string[]>([]);

  const [csvFile, setCsvFile] = createSignal<File | null>(null);
  const [csvHeaders, setCsvHeaders] = createSignal<string[]>([]);
  const [csvRows, setCsvRows] = createSignal<string[][]>([]);
  const [csvMapping, setCsvMapping] = createSignal<PartialCsvMapping>({});

  let pollTimer: ReturnType<typeof setInterval> | null = null;

  const stopPolling = () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  onCleanup(() => {
    stopPolling();
    stopProgressTimer();
  });

  const totalSelected = createMemo(
    () => selectedIssues().size + selectedFlags().size,
  );

  const taskTypeId = createMemo(() => {
    const types = itemTypes();
    const task = types.find((t) => t.name.toLowerCase() === "task");
    return task?.id ?? types[0]?.id;
  });

  // --- Data fetching ---

  const fetchIssuesFromRepo = async (repoId: string) => {
    try {
      const fetched = await fetchGithubIssues(repoId, "open", 1, 100);
      setIssues(fetched);
      setSelectedIssues(new Set(fetched.map((i) => i.number)));
    } catch {
      // Non-fatal — just no issues to show
    }
  };

  const pollForCompletion = (repoId: string) => {
    pollTimer = setInterval(async () => {
      try {
        const result = await getAnalysis(props.projectId, repoId);
        if (result.analysis.status === "complete") {
          stopPolling();
          stopProgressTimer();
          setAnalysisSummary(result.analysis.ai_summary_cache);
          const flags = result.debt_flags.filter((f) => !f.dismissed);
          setDebtFlags(flags);
          setSelectedFlags(new Set(flags.map((f) => f.id)));
          setStepState("results");
        } else if (result.analysis.status === "failed") {
          stopPolling();
          stopProgressTimer();
          setError(result.analysis.error_message || "Analysis failed");
          setStepState("results");
        }
      } catch {
        // Transient error during polling — keep trying
      }
    }, 3000);
  };

  const startAnalysis = async (repoId: string) => {
    setStepState("analyzing");

    // Fetch issues and item types in parallel
    fetchIssuesFromRepo(repoId);
    api
      .get<WorkItemType[]>(`/projects/${props.projectId}/work-items/types`)
      .then(setItemTypes)
      .catch(() => {});

    try {
      const existing = await getAnalysis(props.projectId, repoId);
      if (existing.analysis.status === "complete") {
        setAnalysisSummary(existing.analysis.ai_summary_cache);
        const flags = existing.debt_flags.filter((f) => !f.dismissed);
        setDebtFlags(flags);
        setSelectedFlags(new Set(flags.map((f) => f.id)));
        setStepState("results");
        return;
      }
      if (existing.analysis.status === "running") {
        startProgressTimer();
        pollForCompletion(repoId);
        return;
      }
    } catch {
      // No existing analysis — trigger one
    }

    try {
      await triggerAnalysis(props.projectId, repoId);
      startProgressTimer();
      pollForCompletion(repoId);
    } catch (e) {
      surfaceQuotaExhausted(e);
      setError(e instanceof Error ? e.message : "Failed to start analysis");
      setStepState("results");
    }
  };

  onMount(() => {
    if (props.repoId) {
      startAnalysis(props.repoId);
    } else {
      setStepState("no-repo");
    }
  });

  // --- Debt flag selection ---

  const toggleFlag = (id: string) => {
    setSelectedFlags((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllFlags = () => {
    const all = debtFlags();
    if (selectedFlags().size === all.length) {
      setSelectedFlags(new Set<string>());
    } else {
      setSelectedFlags(new Set(all.map((f) => f.id)));
    }
  };

  // --- Import actions ---

  const handleImport = async () => {
    setStepState("importing");
    setError(null);

    let created = 0;
    const errors: string[] = [];

    // Import selected GitHub issues
    const selIssues = selectedIssues();
    if (selIssues.size > 0 && props.repoId) {
      try {
        const toImport: GitHubIssueImportItem[] = issues()
          .filter((i) => selIssues.has(i.number))
          .map((i) => ({
            number: i.number,
            title: i.title,
            body: i.body,
            labels: i.labels.map((l) => l.name),
            state: i.state,
          }));
        const result = await importGithubIssues(
          props.projectId,
          props.repoId,
          toImport,
        );
        created += result.created;
        if (result.errors.length > 0) {
          errors.push(
            ...result.errors.map((e) => `Issue row ${e.row}: ${e.message}`),
          );
        }
      } catch (e) {
        errors.push(
          `GitHub import: ${e instanceof Error ? e.message : "Failed"}`,
        );
      }
    }

    // Create work items from selected debt flags
    const selFlags = selectedFlags();
    if (selFlags.size > 0) {
      const typeId = taskTypeId();
      if (typeId) {
        for (const flag of debtFlags().filter((f) => selFlags.has(f.id))) {
          try {
            const title = `[Tech Debt] ${flag.debt_type.replace(/_/g, " ")}: ${flag.location || "general"}`;
            const description = [
              flag.description,
              "",
              `**Severity:** ${flag.severity}`,
              `**Type:** ${flag.debt_type.replace(/_/g, " ")}`,
              flag.location ? `**Location:** ${flag.location}` : null,
              "",
              "_Created from repository analysis._",
            ]
              .filter(Boolean)
              .join("\n");

            await api.post<unknown>(`/projects/${props.projectId}/work-items`, {
              title,
              description,
              item_type_id: typeId,
              priority: SEVERITY_TO_PRIORITY[flag.severity] ?? "none",
            } as CreateWorkItemRequest);
            created++;
          } catch (e) {
            errors.push(
              `Debt "${flag.debt_type}": ${e instanceof Error ? e.message : "Failed"}`,
            );
          }
        }
      }
    }

    setImportedCount(created);
    setImportErrors(errors);
    setStepState("done");
  };

  // --- CSV handling ---

  const handleFileParsed = (
    selected: File,
    headers: string[],
    rows: string[][],
  ) => {
    setCsvFile(selected);
    setCsvHeaders(headers);
    setCsvRows(rows);
    setStepState("csv-mapping");
  };

  const handleCsvImport = async () => {
    const mapping = buildCsvMapping(csvMapping());
    const file = csvFile();
    if (!mapping || !file) return;

    setStepState("importing");
    setError(null);
    try {
      const result = await importCsv(props.projectId, file, mapping);
      setImportedCount(result.created);
      setImportErrors(result.errors.map((e) => `Row ${e.row}: ${e.message}`));
      setStepState("done");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import failed");
      setStepState("csv-mapping");
    }
  };

  return (
    <div class="h-full flex items-center justify-center p-8">
      {/* Detecting */}
      <Show when={stepState() === "detecting"}>
        <div class="flex flex-col items-center gap-3">
          <Spinner size="lg" />
          <p class="text-sm text-ink-2">Checking repository...</p>
        </div>
      </Show>

      {/* Analyzing */}
      <Show when={stepState() === "analyzing"}>
        <div class="w-full max-w-md text-center">
          <div class="flex flex-col items-center gap-4">
            <div class="w-12 h-12 bg-signal-magenta-tint rounded-md flex items-center justify-center">
              <svg
                class="w-6 h-6 text-signal-magenta"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
            </div>
            <div>
              <h1 class="text-2xl font-bold text-ink mb-2">
                Analyzing your repository
              </h1>
              <p class="text-ink-2 mb-4">
                Scanning{" "}
                <Show when={props.repoOwner && props.repoName}>
                  <span class="font-medium">
                    {props.repoOwner}/{props.repoName}
                  </span>
                </Show>{" "}
                for architecture, components, and technical debt.
              </p>

              {/* Progress bar */}
              <div class="w-full bg-sunken rounded-full h-2 mb-2 overflow-hidden">
                <Show
                  when={!progressIndeterminate()}
                  fallback={
                    <div class="h-full w-1/3 bg-ink rounded-full animate-[indeterminate_1.5s_ease-in-out_infinite]" />
                  }
                >
                  <div
                    class="h-full bg-ink rounded-full transition-[width] duration-300 ease-out"
                    style={{ width: `${progressPct()}%` }}
                  />
                </Show>
              </div>
              <p class="text-sm text-ink-3">{progressLabel()}</p>
            </div>
            <Button variant="ghost" size="md" onClick={props.onSkip}>
              Skip for now
            </Button>
          </div>
        </div>
      </Show>

      {/* Results */}
      <Show when={stepState() === "results"}>
        <div class="w-full max-w-lg">
          <div class="text-center mb-6">
            <div class="w-12 h-12 bg-signal-magenta-tint rounded-md flex items-center justify-center mx-auto mb-4">
              <svg
                class="w-6 h-6 text-signal-magenta"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
            </div>
            <h1 class="text-2xl font-bold text-ink mb-2">Seed your board</h1>
            <p class="text-ink-2">
              <Show
                when={issues().length > 0 || debtFlags().length > 0}
                fallback="No items found to import. You can upload a CSV or skip."
              >
                Select items to add to your board.
              </Show>
            </p>
          </div>

          <Show when={error()}>
            <div class="px-3 py-2 mb-4 bg-signal-amber-tint border border-signal-amber/25 rounded text-sm text-signal-amber">
              {error()}
            </div>
          </Show>

          {/* Analysis summary */}
          <Show when={analysisSummary()}>
            <div class="bg-signal-magenta-tint border border-signal-magenta/25 rounded p-3 mb-4">
              <p class="text-xs font-medium text-signal-magenta mb-1">
                Architecture Summary
              </p>
              <p class="text-sm text-ink-2 line-clamp-3">{analysisSummary()}</p>
            </div>
          </Show>

          {/* GitHub Issues section */}
          <Show when={issues().length > 0}>
            <div class="bg-surface rounded border border-line mb-4">
              <GithubIssuePicker
                issues={issues()}
                selected={selectedIssues()}
                onSelectionChange={setSelectedIssues}
                listClass="max-h-48"
                headerClass="flex items-center justify-between px-4 py-2 border-b border-line"
                label={
                  <span class="text-sm font-medium text-ink-2">
                    GitHub Issues ({issues().length})
                  </span>
                }
              />
            </div>
          </Show>

          {/* Suggested Tasks (debt flags) */}
          <Show when={debtFlags().length > 0}>
            <div class="bg-surface rounded border border-line mb-4">
              <div class="flex items-center justify-between px-4 py-2 border-b border-line">
                <span class="text-sm font-medium text-ink-2">
                  Suggested Tasks ({debtFlags().length})
                </span>
                <button
                  class="text-xs font-medium text-ink-2 hover:text-ink"
                  onClick={toggleAllFlags}
                >
                  <Show
                    when={selectedFlags().size === debtFlags().length}
                    fallback={<>Select all</>}
                  >
                    Deselect all
                  </Show>
                </button>
              </div>
              <div class="max-h-48 overflow-y-auto divide-y divide-line">
                <For each={debtFlags()}>
                  {(flag) => (
                    <label class="flex items-start gap-3 px-4 py-2 cursor-pointer hover:bg-sunken transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedFlags().has(flag.id)}
                        onChange={() => toggleFlag(flag.id)}
                        class="mt-0.5 accent-ink rounded"
                      />
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-2">
                          <span
                            class={`text-xs px-1.5 py-0.5 rounded border shrink-0 ${SEVERITY_COLORS[flag.severity] || ""}`}
                          >
                            {flag.severity}
                          </span>
                          <span class="text-sm text-ink-2 truncate">
                            {flag.debt_type.replace(/_/g, " ")}
                          </span>
                        </div>
                        <p class="text-xs text-ink-3 mt-0.5 line-clamp-1">
                          {flag.description}
                        </p>
                        <Show when={flag.location}>
                          <p class="text-xs text-ink-3 mt-0.5">
                            {flag.location}
                          </p>
                        </Show>
                      </div>
                    </label>
                  )}
                </For>
              </div>
            </div>
          </Show>

          {/* Actions */}
          <div class="flex flex-col gap-3">
            <Show when={totalSelected() > 0}>
              <Button
                variant="primary"
                size="lg"
                class="w-full"
                onClick={handleImport}
              >
                Import {totalSelected()} Item{totalSelected() !== 1 ? "s" : ""}
              </Button>
            </Show>
            <Show
              when={
                totalSelected() === 0 &&
                (issues().length > 0 || debtFlags().length > 0)
              }
            >
              <Button
                variant="primary"
                size="lg"
                class="w-full"
                onClick={props.onComplete}
              >
                Continue
              </Button>
            </Show>
            <div class="flex items-center gap-3">
              <Button
                variant="ghost"
                size="md"
                class="flex-1"
                onClick={() => setStepState("csv-upload")}
              >
                Import CSV instead
              </Button>
              <Button
                variant="ghost"
                size="md"
                class="flex-1"
                onClick={props.onSkip}
              >
                Skip for now
              </Button>
            </div>
          </div>
        </div>
      </Show>

      {/* No repo — offer CSV or skip */}
      <Show when={stepState() === "no-repo"}>
        <div class="w-full max-w-md text-center">
          <div class="mb-6">
            <div class="w-12 h-12 bg-sunken rounded-md flex items-center justify-center mx-auto mb-4">
              <svg
                class="w-6 h-6 text-ink-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
            </div>
            <h1 class="text-2xl font-bold text-ink mb-2">
              Import existing tasks
            </h1>
            <p class="text-ink-2">
              Bring your existing work items into Flowicity from a CSV file.
            </p>
          </div>

          <div class="flex flex-col gap-3">
            <Button
              variant="primary"
              size="lg"
              class="w-full"
              onClick={() => setStepState("csv-upload")}
            >
              Upload CSV File
            </Button>
            <Button
              variant="ghost"
              size="md"
              class="w-full"
              onClick={props.onSkip}
            >
              Skip for now
            </Button>
            <p class="text-xs text-ink-3">
              You can always import tasks later from the board menu.
            </p>
          </div>
        </div>
      </Show>

      {/* CSV Upload */}
      <Show when={stepState() === "csv-upload"}>
        <div class="w-full max-w-md text-center">
          <div class="mb-6">
            <div class="w-12 h-12 bg-sunken rounded-md flex items-center justify-center mx-auto mb-4">
              <svg
                class="w-6 h-6 text-ink-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
            </div>
            <h1 class="text-2xl font-bold text-ink mb-2">Import from CSV</h1>
            <p class="text-ink-2">
              Upload a CSV file with your tasks. The first row should be a
              header row.
            </p>
          </div>

          <CsvDropzone onFileParsed={handleFileParsed} class="mb-6" />

          <div class="flex flex-col gap-3">
            <Button
              variant="ghost"
              size="md"
              class="w-full"
              onClick={() => setStepState(props.repoId ? "results" : "no-repo")}
            >
              Back
            </Button>
            <Button
              variant="ghost"
              size="md"
              class="w-full"
              onClick={props.onSkip}
            >
              Skip for now
            </Button>
          </div>
        </div>
      </Show>

      {/* CSV Mapping */}
      <Show when={stepState() === "csv-mapping"}>
        <div class="w-full max-w-md">
          <div class="text-center mb-6">
            <h1 class="text-2xl font-bold text-ink mb-2">Map your columns</h1>
            <p class="text-ink-2">
              Map CSV columns to work item fields. Title is required.
            </p>
          </div>

          <Show when={error()}>
            <div class="px-3 py-2 mb-4 bg-signal-red-tint border border-signal-red/25 rounded text-sm text-signal-red">
              {error()}
            </div>
          </Show>

          <div class="bg-surface rounded border border-line p-6 mb-6">
            <CsvMappingForm
              headers={csvHeaders()}
              mapping={csvMapping()}
              onChange={setCsvMapping}
            />
          </div>

          <p class="text-xs text-ink-3 mb-4">
            {csvRows().length} rows to import.
          </p>

          <div class="flex gap-3">
            <Button
              variant="ghost"
              class="flex-1"
              onClick={() => {
                setCsvFile(null);
                setCsvHeaders([]);
                setCsvRows([]);
                setCsvMapping({});
                setStepState("csv-upload");
              }}
            >
              Back
            </Button>
            <Button
              variant="primary"
              class="flex-1"
              disabled={csvMapping().title === undefined}
              onClick={handleCsvImport}
            >
              Import
            </Button>
          </div>
        </div>
      </Show>

      {/* Importing */}
      <Show when={stepState() === "importing"}>
        <div class="flex flex-col items-center gap-3">
          <Spinner size="lg" />
          <p class="text-sm text-ink-2">Importing your items...</p>
        </div>
      </Show>

      {/* Done */}
      <Show when={stepState() === "done"}>
        <div class="w-full max-w-md text-center">
          <div class="mb-6">
            <div class="w-12 h-12 bg-signal-green-tint rounded-md flex items-center justify-center mx-auto mb-4">
              <svg
                class="w-6 h-6 text-signal-green"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 class="text-2xl font-bold text-ink mb-2">Import complete!</h1>
            <p class="text-ink-2">
              <span class="font-semibold text-signal-green">
                {importedCount()}
              </span>{" "}
              item{importedCount() !== 1 ? "s" : ""} added to your board.
            </p>
            <Show when={importErrors().length > 0}>
              <div class="rounded border border-signal-amber/25 bg-signal-amber-tint p-3 text-left mt-3">
                <p class="text-xs font-medium text-signal-amber mb-1">
                  {importErrors().length} error(s):
                </p>
                <For each={importErrors().slice(0, 5)}>
                  {(err) => <p class="text-xs text-signal-amber">{err}</p>}
                </For>
                <Show when={importErrors().length > 5}>
                  <p class="text-xs text-signal-amber mt-1">
                    ...and {importErrors().length - 5} more
                  </p>
                </Show>
              </div>
            </Show>
          </div>

          <Button
            variant="primary"
            size="lg"
            class="w-full"
            onClick={props.onComplete}
          >
            Continue
          </Button>
        </div>
      </Show>
    </div>
  );
};
